import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Avatar,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Notifications,
  Message,
  Schedule,
  School,
  Person,
  MarkEmailRead,
  Clear,
  Settings,
} from '@mui/icons-material';
import { notificationService, type Notification } from '../services/notificationService';
import { authService } from '../services/authService';

// Notification interface is imported from notificationService

interface NotificationSystemProps {
  userType: 'parent' | 'teacher' | 'student';
}

const NotificationSystem: React.FC<NotificationSystemProps> = ({ userType }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Only load notifications if user is authenticated
    if (authService.isAuthenticated()) {
      loadNotifications();

      // Refresh notifications every 30 minutes
      const thirtyMinutesMs = 30 * 60 * 1000;
      const interval = setInterval(() => {
        if (authService.isAuthenticated()) {
          loadNotifications();
        }
      }, thirtyMinutesMs);

      return () => clearInterval(interval);
    }
  }, [userType]);

  const loadNotifications = async () => {
    // Double-check authentication before making API call
    if (!authService.isAuthenticated()) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const notificationSummary = await notificationService.getNotificationSummary();
      setNotifications(notificationSummary.recentNotifications);
    } catch (err: any) {
      // Don't show error if it's a 401 (unauthorized) - user will be redirected
      if (err.response?.status !== 401) {
        console.error('Error loading notifications:', err);
        setError('Failed to load notifications');
      }
      // Fallback to empty array on error
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification: Notification) => {
    try {
      // Mark as read if not already read
      if (!notification.isRead) {
        await notificationService.markAsRead(notification.id);
        setNotifications(prev => 
          prev.map(n => n.id === notification.id ? { ...n, isRead: true } : n)
        );
      }

      // Navigate to action URL if available
      if (notification.actionUrl) {
        window.location.href = notification.actionUrl;
      }

      handleClose();
    } catch (err: any) {
      console.error('Error marking notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch (err: any) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  const clearAll = async () => {
    try {
      await notificationService.clearAllNotifications();
      setNotifications([]);
    } catch (err: any) {
      console.error('Error clearing all notifications:', err);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'message':
        return <Message sx={{ fontSize: 20 }} />;
      case 'meeting':
        return <Schedule sx={{ fontSize: 20 }} />;
      case 'achievement':
        return <School sx={{ fontSize: 20 }} />;
      default:
        return <Notifications sx={{ fontSize: 20 }} />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'normal':
        return 'primary';
      default:
        return 'default';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const getAvatarContent = (notification: Notification) => {
    if (notification.fromUserAvatar) {
      return notification.fromUserAvatar;
    }
    return getNotificationIcon(notification.type);
  };

  return (
    <>
      <IconButton onClick={handleClick} color="inherit">
        <Badge badgeContent={unreadCount} color="error">
          <Notifications />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: { width: 400, maxHeight: 600 }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>
          <Box>
            {unreadCount > 0 && (
              <Button size="small" onClick={markAllAsRead}>
                <MarkEmailRead sx={{ fontSize: 16, mr: 0.5 }} />
                Mark all read
              </Button>
            )}
            <IconButton size="small" onClick={clearAll}>
              <Clear />
            </IconButton>
          </Box>
        </Box>
        <Divider />

        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Loading notifications...
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button size="small" onClick={loadNotifications}>
              Retry
            </Button>
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Notifications sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No notifications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You're all caught up!
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0, maxHeight: 400, overflowY: 'auto' }}>
            {notifications.slice(0, 10).map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem 
                  button 
                  onClick={() => handleNotificationClick(notification)}
                  sx={{ 
                    py: 2,
                    bgcolor: notification.isRead ? 'transparent' : 'primary.50',
                    '&:hover': { bgcolor: 'grey.100' }
                  }}
                >
                  <ListItemAvatar>
                    <Box sx={{ position: 'relative' }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: notification.fromUserAvatar ? 'secondary.main' : 'primary.main',
                          width: 40, 
                          height: 40 
                        }}
                      >
                        {getAvatarContent(notification)}
                      </Avatar>
                      {!notification.isRead && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: 12,
                            height: 12,
                            bgcolor: 'error.main',
                            borderRadius: '50%',
                            border: '2px solid white'
                          }}
                        />
                      )}
                    </Box>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: notification.isRead ? 400 : 600,
                            flex: 1
                          }}
                        >
                          {notification.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTimestamp(notification.createdAt)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            mb: 0.5
                          }}
                        >
                          {notification.message}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip 
                            label={notification.type} 
                            size="small" 
                            color={getPriorityColor(notification.priority) as any}
                            variant="outlined"
                          />
                          {notification.priority === 'high' && (
                            <Chip label="High Priority" color="error" size="small" />
                          )}
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
                {index < notifications.slice(0, 10).length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}

        {notifications.length > 10 && (
          <>
            <Divider />
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Button variant="text" size="small">
                View all {notifications.length} notifications
              </Button>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
};

export default NotificationSystem;
